import { useEffect, useState } from 'react';

import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';

export function PHProvider({ children }: { children: React.ReactNode }) {
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    // Get PostHog configuration from environment variables or hardcoded values
    const posthogKey = window.ENV?.POSTHOG_API_KEY || 'phc_RFwLyKCAoj0exm4p8dAUKFHm7NxUMEy1fKbbhIJMV2z';
    const posthogHost = window.ENV?.POSTHOG_HOST || 'https://us.i.posthog.com';
    const isDevMode = window.location.hostname === 'localhost' || window.location.hostname.includes('127.0.0.1');

    // Only initialize PostHog if we have the required environment variables
    if (posthogKey && posthogHost) {
      console.log('Initializing PostHog with:', { posthogKey, posthogHost });
      posthog.init(posthogKey, {
        api_host: posthogHost,
        person_profiles: 'identified_only',
        
        // Enable session recording/replays (enabled by providing this configuration)
        session_recording: {
          maskAllInputs: false, // Set to true to mask all input fields
          maskInputOptions: {
            password: true, // Always mask password fields
          },
        },
        
        // Enable automatic event capture (critical for mobile)
        autocapture: {
          dom_event_allowlist: ['click', 'change', 'submit'], // Mobile touch events are auto-converted to clicks by PostHog
          url_allowlist: undefined, // Capture from all URLs
          element_allowlist: undefined, // Capture all elements
          capture_copied_text: false, // Set to true to capture copied text
          css_selector_allowlist: undefined, // Capture all CSS selectors
        },
        
        // Capture page views automatically
        capture_pageview: true,
        capture_pageleave: true,
        
        // Mobile-specific settings
        loaded: (ph) => {
          const isMobile = window.innerWidth < 768;
          console.log('PostHog loaded successfully', {
            isMobile,
            screenWidth: window.innerWidth,
            userAgent: navigator.userAgent.substring(0, 50) + '...',
          });
          
          // Ensure session replay is active
          if (ph.sessionRecording) {
            console.log('Session recording is active');
          } else {
            console.warn('Session recording failed to initialize');
          }
          
          // Test mobile event capture
          if (isMobile) {
            console.log('Mobile device detected - PostHog mobile tracking enabled');
            // Send a test event to verify mobile tracking works
            ph.capture('posthog_mobile_initialized', {
              device_type: 'mobile',
              screen_width: window.innerWidth,
              screen_height: window.innerHeight,
            });
          }
        },
        
        // Advanced settings for better mobile support
        persistence: 'localStorage+cookie', // Use both for better mobile support
        cross_subdomain_cookie: false,
        secure_cookie: true, // Important for HTTPS sites
        
        // Disable features that can cause issues on mobile
        disable_session_recording: false,
        disable_surveys: false,
        disable_compression: false, // Keep compression for mobile bandwidth
        
        // Debug mode - enable for localhost only
        debug: isDevMode,
      });
      console.log('PostHog initialized successfully');

      // Test if PostHog is working
      setTimeout(() => {
        try {
          posthog.capture('test_event', { test: true });
          console.log('PostHog test event sent successfully');
        } catch (error) {
          console.error('PostHog test event failed:', error);
        }
      }, 1000);
    } else {
      console.warn(
        'PostHog not initialized: Missing POSTHOG_API_KEY or POSTHOG_HOST environment variables'
      );
    
    }

    setHydrated(true);
  }, []);

  if (!hydrated) return <>{children}</>;
  return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
}
